import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';
import OasisBoard from './OasisBoard.js';
import OasisBoardSkeleton from './OasisBoardSkeleton.js';

export default function OnlineGame() {
  const { gameId } = useParams();
  const { currentUser } = useAuth();
  const [publicDoc, setPublicDoc] = useState(undefined);
  const [view, setView] = useState(undefined);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [playerNames, setPlayerNames] = useState({});

  // Body background is orange site-wide, but this page's own background is
  // a lighter shade -- toggle a class on <body> (rather than styling body
  // itself in CSS) so overscroll/safe-area slivers on mobile show the
  // lighter shade too, without affecting every other page.
  useEffect(() => {
    document.body.classList.add('online-game-active');
    return () => document.body.classList.remove('online-game-active');
  }, []);

  useEffect(() => {
    const unsubPublic = onSnapshot(doc(db, 'onlineGames', gameId), snap => {
      setPublicDoc(snap.exists() ? snap.data() : null);
    });
    return unsubPublic;
  }, [gameId]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const unsubView = onSnapshot(doc(db, 'onlineGames', gameId, 'views', currentUser.uid), snap => {
      setView(snap.exists() ? snap.data() : null);
    });
    return unsubView;
    // currentUser is a new object reference on every auth-state event (not
    // just sign-in/out -- e.g. token refresh, or a multi-tab reconcile), so
    // depending on the whole object tears down and recreates this realtime
    // listener far more often than the uid actually changes -- each cycle
    // is a window where a board update can be missed until something else
    // (like the opponent's next move re-writing this same doc) happens to
    // resync it. Depending on just the uid keeps the listener alive across
    // those events instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, currentUser?.uid]);

  useEffect(() => {
    if (!publicDoc) return;
    Promise.all([
      getDoc(doc(db, 'players', publicDoc.player1Id)),
      getDoc(doc(db, 'players', publicDoc.player2Id)),
    ]).then(([p1Snap, p2Snap]) => {
      setPlayerNames({
        player1: p1Snap.exists() ? p1Snap.data().name : '',
        player2: p2Snap.exists() ? p2Snap.data().name : '',
      });
    });
  }, [publicDoc]);

  async function handleSubmitMove(payload) {
    setSubmitting(true);
    setError(null);
    try {
      const submitMove = httpsCallable(functions, 'submitMove');
      await submitMove({ gameId, ...payload });
    } catch (err) {
      setError(err.message);
      throw err; // let the board know the move didn't go through, so it keeps it staged
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitRoundEndChoice(kind, choice) {
    setError(null);
    try {
      const submitRoundEndChoice = httpsCallable(functions, 'submitRoundEndChoice');
      await submitRoundEndChoice({ gameId, kind, choice });
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAcknowledgeRoundEnd() {
    setAcknowledging(true);
    setError(null);
    try {
      const acknowledgeRoundEnd = httpsCallable(functions, 'acknowledgeRoundEnd');
      await acknowledgeRoundEnd({ gameId });
    } catch (err) {
      setError(err.message);
    } finally {
      setAcknowledging(false);
    }
  }

  return (
    <div className="online-game-page">
      <div className="online-game-page-topbar">
        <Link to="/online" className="online-game-page-back">← My Games</Link>
      </div>

      {error && (
        <Alert variant="danger" className="online-game-page-alert" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {publicDoc === undefined || view === undefined ? (
        <div className="online-game-page-board">
          <OasisBoardSkeleton />
        </div>
      ) : !publicDoc || !view ? (
        <p>Game not found.</p>
      ) : (
        (() => {
          const mySide = publicDoc.player1Uid === currentUser.uid ? 'player1' : 'player2';
          const opponentSide = mySide === 'player1' ? 'player2' : 'player1';
          const myId = publicDoc[`${mySide}Id`];
          const myTurn = publicDoc.currentTurnPlayerId === myId && publicDoc.status !== 'completed';
          const myName = playerNames[mySide] ?? '';
          const opponentName = playerNames[opponentSide] ?? '';
          const myScore = publicDoc.scores[mySide];
          const opponentScore = publicDoc.scores[opponentSide];

          // A round just ended but this player hasn't clicked through it yet
          // -- tracked server-side (view.ackedRound) rather than in local
          // state, so a refresh brings the review back instead of skipping
          // straight to the next round (or final results).
          const lastRoundResult = view.lastRoundResult;
          const reviewPending = Boolean(lastRoundResult) && view.ackedRound < lastRoundResult.round;

          if (publicDoc.status === 'completed' && !reviewPending) {
            const result = myScore > opponentScore ? 'You won!' : myScore < opponentScore ? 'You lost.' : "It's a tie.";
            return (
              <div className="online-game-page-result">
                <h2>{result}</h2>
                <p>Final score: {myScore} to {opponentScore}</p>
                <Button as={Link} to="/online">Back to Games</Button>
              </div>
            );
          }

          // While reviewing a just-finished round, show that round's final
          // (fully resolved) board instead of the live one -- the next
          // round's hand has often already been dealt server-side by now,
          // so it's deliberately hidden here rather than shown early.
          const displayView = reviewPending ? { hand: [], oasisRow: lastRoundResult.oasisRow } : view;
          const displayRound = reviewPending ? lastRoundResult.round : publicDoc.round;

          const roundSummary = reviewPending
            ? lastRoundResult.roundPoints.my === lastRoundResult.roundPoints.opponent
              ? `Round ${lastRoundResult.round} was a tie.`
              : lastRoundResult.roundPoints.my > lastRoundResult.roundPoints.opponent
              ? `You won round ${lastRoundResult.round}! (${lastRoundResult.roundPoints.my} to ${lastRoundResult.roundPoints.opponent})`
              : `${opponentName} won round ${lastRoundResult.round}. (${lastRoundResult.roundPoints.my} to ${lastRoundResult.roundPoints.opponent})`
            : null;

          return (
            <div className="online-game-page-board">
              <OasisBoard
                view={displayView}
                myTurn={!reviewPending && myTurn}
                myName={myName}
                opponentName={opponentName}
                round={displayRound}
                myScore={myScore}
                opponentScore={opponentScore}
                opponentHandCount={publicDoc[`${opponentSide}HandCount`]}
                myPalmTreesRemaining={publicDoc[`${mySide}PalmTreesRemaining`]}
                opponentPalmTreesRemaining={publicDoc[`${opponentSide}PalmTreesRemaining`]}
                deckCount={reviewPending && lastRoundResult.deckCount !== null ? lastRoundResult.deckCount : publicDoc.deckCount}
                burnedCard={view.burnedCard}
                lastMoveCardIds={reviewPending ? lastRoundResult.flippedCardIds : publicDoc.lastMoveCardIds ?? []}
                revealResult={reviewPending}
                roundSummary={roundSummary}
                onNextRound={reviewPending ? handleAcknowledgeRoundEnd : undefined}
                nextRoundButtonLabel={lastRoundResult?.round >= 3 ? 'See Final Results' : 'Next Round'}
                nextRoundDisabled={acknowledging}
                onSubmitMove={handleSubmitMove}
                submitting={submitting}
                roundEndResolution={view.roundEndResolution}
                onSubmitRoundEndChoice={handleSubmitRoundEndChoice}
                lastRoundResult={lastRoundResult}
                lastMoveResult={view.lastMoveResult}
              />
            </div>
          );
        })()
      )}
    </div>
  );
}
